/* Miniversos wishlist client.
 * Dependency-free. Configured by the wishlist-init snippet via
 * window.MV_WISHLIST_CFG = { proxy: "/apps/wishlist", customerId: <id|null> }.
 *
 * Guests: wishlist lives in localStorage only (ids + product snapshots taken
 * from the heart button's data attributes).
 * Logged-in customers: the Shopify app proxy is the source of truth;
 * localStorage acts as a cache and is reconciled from every proxy response.
 * On login, a non-empty guest list is merged into the account once.
 */
(function () {
  'use strict';

  var CFG = window.MV_WISHLIST_CFG || { proxy: '/apps/wishlist', customerId: null };
  // Locale-aware URL root: '' on the default language, '/en' on English pages.
  var ROOT = CFG.rootUrl === '/' || !CFG.rootUrl ? '' : CFG.rootUrl.replace(/\/$/, '');
  // Strings come from the init snippet via the theme's locale files; the
  // fallbacks match the store's default language (English).
  var I18N = CFG.i18n || {};
  function t(key, fallback) {
    return I18N[key] || fallback;
  }
  var T_ADD = t('add', 'Add to Wishlist');
  var T_REMOVE = t('remove', 'Remove from Wishlist');
  var STORAGE_KEY = 'mv_wishlist';
  var SESSION_HYDRATED_KEY = 'mv_wishlist_hydrated';
  var ACTIVE_CLASS = 'mv-active';
  var MAX_ITEMS = 200; // must match the server-side MAX_WISHLIST_ITEMS

  // ── localStorage store ────────────────────────────────────────────────────

  function emptyStore() {
    return { v: 1, mergedCustomerId: null, items: [] };
  }

  function readStore() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return emptyStore();
      var store = JSON.parse(raw);
      if (!store || store.v !== 1 || !Array.isArray(store.items)) return emptyStore();
      return store;
    } catch (e) {
      return emptyStore();
    }
  }

  function writeStore(store) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (e) {
      /* storage full / unavailable — wishlist degrades to per-page state */
    }
  }

  function storeIds(store) {
    return store.items.map(function (item) { return item.id; });
  }

  function hasId(store, id) {
    return storeIds(store).indexOf(id) !== -1;
  }

  // Keep snapshots for ids the server confirmed; ids we have no snapshot for
  // (added on another device) are kept as id-only entries so hearts still fill.
  function reconcile(store, serverIds) {
    var byId = {};
    store.items.forEach(function (item) { byId[item.id] = item; });
    store.items = serverIds.map(function (id) {
      return byId[id] || { id: id, addedAt: Math.floor(Date.now() / 1000) };
    });
    writeStore(store);
    return store;
  }

  // ── Proxy API ─────────────────────────────────────────────────────────────

  function api(path, body) {
    var options = { credentials: 'same-origin' };
    if (body !== undefined) {
      options.method = 'POST';
      options.headers = { 'Content-Type': 'application/json' };
      options.body = JSON.stringify(body);
    }
    return fetch(CFG.proxy + path, options).then(function (response) {
      if (!response.ok) {
        var error = new Error('wishlist request failed: ' + response.status);
        error.status = response.status;
        throw error;
      }
      return response.json();
    });
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  function toast(message) {
    var el = document.createElement('div');
    el.className = 'mv-wishlist-toast';
    el.setAttribute('role', 'status');
    el.textContent = message;
    document.body.appendChild(el);
    window.setTimeout(function () {
      el.classList.add('mv-wishlist-toast--out');
      window.setTimeout(function () { el.remove(); }, 400);
    }, 3000);
  }

  function paintButtons() {
    var ids = storeIds(readStore());
    var buttons = document.querySelectorAll('[data-mv-wishlist-toggle]');
    Array.prototype.forEach.call(buttons, function (button) {
      var id = Number(button.getAttribute('data-product-id'));
      var active = ids.indexOf(id) !== -1;
      button.classList.toggle(ACTIVE_CLASS, active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? T_REMOVE : T_ADD);
    });
  }

  function snapshotFromButton(button) {
    return {
      id: Number(button.getAttribute('data-product-id')),
      handle: button.getAttribute('data-product-handle') || '',
      title: button.getAttribute('data-product-title') || '',
      image: button.getAttribute('data-product-image') || '',
      price: button.getAttribute('data-product-price') || '',
      variantId: Number(button.getAttribute('data-product-variant-id')) || null,
      singleVariant: button.getAttribute('data-product-single-variant') === 'true',
      addedAt: Math.floor(Date.now() / 1000)
    };
  }

  function cartAdd(variantId, button) {
    button.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart add failed: ' + response.status);
        return response.json();
      })
      .then(function () {
        toast(t('addedToCart', 'Added to cart.'));
        // the theme's cart drawer/header only re-render on their own requests
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        try { localStorage.setItem('cart:stamp', String(Date.now())); } catch (e) { /* best-effort cross-tab sync */ }
      })
      .catch(function () {
        toast(t('cartError', 'Could not add to cart.'));
      })
      .then(function () { button.disabled = false; });
  }

  // On the wishlist page a successful remove also drops the card from the DOM.
  function removeCardFor(button) {
    var card = button.closest('[data-mv-wishlist-card]');
    if (!card) return;
    var grid = card.parentElement;
    card.remove();
    if (grid && !grid.querySelector('[data-mv-wishlist-card]')) {
      var empty = document.createElement('p');
      empty.className = 'mv-wishlist-empty';
      empty.textContent = t('empty', 'Your wishlist is empty');
      grid.insertAdjacentElement('afterend', empty);
    }
  }

  // ── Guest wishlist page rendering ─────────────────────────────────────────

  // Upgrade a guest snapshot card to the real theme product card, fetched via
  // the section rendering API (same mechanism the theme's browsing-history
  // uses). On failure the snapshot placeholder simply stays.
  function themeCardSwap(wrap, handle) {
    fetch(ROOT + '/products/' + encodeURIComponent(handle) + '?section_id=r_product-card')
      .then(function (response) {
        if (!response.ok) throw new Error('card fetch failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var tpl = doc.querySelector('template[data-req-content]');
        if (!tpl || !tpl.content || !tpl.content.firstElementChild) return;
        wrap.innerHTML = '';
        wrap.appendChild(tpl.content.cloneNode(true));
        paintButtons();
      })
      .catch(function () { /* snapshot card stays */ });
  }

  function renderGuestGrid() {
    var grid = document.querySelector('[data-mv-wishlist-guest-grid]');
    if (!grid) return;
    var store = readStore();
    grid.innerHTML = '';
    if (store.items.length === 0) {
      var empty = document.createElement('p');
      empty.className = 'mv-wishlist-empty';
      empty.textContent = t('empty', 'Your wishlist is empty');
      grid.insertAdjacentElement('afterend', empty);
      return;
    }
    store.items.forEach(function (item) {
      var wrap = document.createElement('div');
      wrap.setAttribute('data-mv-wishlist-card', '');

      var card = document.createElement('div');
      card.className = 'mv-wishlist-card';

      var link = document.createElement('a');
      link.href = item.handle ? ROOT + '/products/' + item.handle : '#';
      if (item.image) {
        var img = document.createElement('img');
        img.src = item.image;
        img.alt = item.title || '';
        img.loading = 'lazy';
        link.appendChild(img);
      }
      var title = document.createElement('p');
      title.className = 'mv-wishlist-card__title';
      title.textContent = item.title || '';
      link.appendChild(title);
      var price = document.createElement('p');
      price.className = 'mv-wishlist-card__price';
      price.textContent = item.price || '';
      link.appendChild(price);
      card.appendChild(link);

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'mv-wishlist-toggle ' + ACTIVE_CLASS;
      button.setAttribute('data-mv-wishlist-toggle', '');
      button.setAttribute('data-product-id', String(item.id));
      button.setAttribute('data-product-handle', item.handle || '');
      button.setAttribute('data-product-title', item.title || '');
      button.setAttribute('data-product-image', item.image || '');
      button.setAttribute('data-product-price', item.price || '');
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', T_REMOVE);
      button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 21s-6.7-4.35-9.33-8.11C.9 10.34 1.7 6.86 4.56 5.5c2.04-.97 4.46-.33 5.9 1.28L12 8.35l1.54-1.57c1.44-1.61 3.86-2.25 5.9-1.28 2.86 1.36 3.66 4.84 1.89 7.39C18.7 16.65 12 21 12 21z"/></svg>';
      card.appendChild(button);

      // Single-variant snapshots get a direct add-to-cart; anything else
      // (multi-variant, sold out at add-time, pre-variant-data snapshots)
      // links through to the product page.
      var action;
      if (item.variantId && item.singleVariant) {
        action = document.createElement('button');
        action.type = 'button';
        action.className = 'mv-wishlist-card__atc';
        action.textContent = t('addToCart', 'Add to cart');
        action.addEventListener('click', function () { cartAdd(item.variantId, action); });
      } else if (item.handle) {
        action = document.createElement('a');
        action.className = 'mv-wishlist-card__atc';
        action.href = ROOT + '/products/' + item.handle;
        action.textContent = t('viewOptions', 'View options');
      }
      if (action) card.appendChild(action);

      wrap.appendChild(card);
      grid.appendChild(wrap);
      if (item.handle) themeCardSwap(wrap, item.handle);
    });
  }

  // ── Toggle ────────────────────────────────────────────────────────────────

  var busy = {};

  function handleToggle(button) {
    var id = Number(button.getAttribute('data-product-id'));
    if (!id || busy[id]) return;
    var store = readStore();
    var isRemove = hasId(store, id);

    if (CFG.customerId == null) {
      // Guest: localStorage only.
      if (isRemove) {
        store.items = store.items.filter(function (item) { return item.id !== id; });
        writeStore(store);
        removeCardFor(button);
      } else {
        if (store.items.length >= MAX_ITEMS) {
          toast(t('full', 'Your wishlist is full.'));
          return;
        }
        store.items.push(snapshotFromButton(button));
        writeStore(store);
      }
      paintButtons();
      return;
    }

    // Logged-in: proxy is the source of truth; reconcile cache from response.
    busy[id] = true;
    var snapshot = isRemove ? null : snapshotFromButton(button);
    api(isRemove ? '/remove' : '/add', { productId: id })
      .then(function (data) {
        var current = readStore();
        if (snapshot && current.items.every(function (item) { return item.id !== id; })) {
          current.items.push(snapshot);
        }
        reconcile(current, data.items);
        if (isRemove) removeCardFor(button);
        paintButtons();
      })
      .catch(function (error) {
        if (error && error.status === 422) {
          toast(t('full', 'Your wishlist is full.'));
        } else {
          toast(t('error', 'Could not update your wishlist.'));
        }
      })
      .then(function () { delete busy[id]; });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-mv-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    handleToggle(button);
  });

  // ── Merge-on-login / hydration ────────────────────────────────────────────

  function markMergedAndReconcile(serverIds) {
    var current = readStore();
    current.mergedCustomerId = CFG.customerId;
    reconcile(current, serverIds);
    try { window.sessionStorage.setItem(SESSION_HYDRATED_KEY, '1'); } catch (e) { /* ignore */ }
    paintButtons();
  }

  function hydrateFromServer() {
    return api('/list').then(function (data) {
      markMergedAndReconcile(data.items);
    });
  }

  // The init snippet inlines the metafield's ids at page render (CFG.serverIds),
  // making hydration free; the network read is only a fallback for templates
  // rendered by an older snippet version.
  function hydrate() {
    if (Array.isArray(CFG.serverIds)) {
      markMergedAndReconcile(CFG.serverIds);
      return Promise.resolve();
    }
    return hydrateFromServer();
  }

  function init() {
    if (CFG.customerId != null) {
      var store = readStore();
      if (store.mergedCustomerId != null && store.mergedCustomerId !== CFG.customerId) {
        // A different customer used this browser before (shared machine).
        // Their cached list must NEVER merge into this account — reset the
        // local store and hydrate this customer's own list from the server.
        store.items = [];
        store.mergedCustomerId = null;
        writeStore(store);
        hydrate().catch(function () { /* hearts stay empty until next load */ });
      } else if (store.mergedCustomerId === null && store.items.length > 0) {
        // Genuine guest list → one-time merge into the account. The server
        // merge is convergent (fills up to the cap, drops overflow), so any
        // completed response marks the merge done.
        api('/merge', { productIds: storeIds(store) })
          .then(function (data) {
            markMergedAndReconcile(data.items);
          })
          .catch(function (error) {
            if (error && error.status >= 400 && error.status < 500) {
              // Rejected input can never succeed on retry — mark merged so we
              // don't loop on every page load, and fall back to the server list.
              // (hydrate() may reflect the pre-merge render here; the next page
              // load reconciles to the true server state.)
              hydrate().catch(function () {
                var current = readStore();
                current.mergedCustomerId = CFG.customerId;
                writeStore(current);
              });
            } else {
              // Transient (network / 5xx): keep state untouched so the next
              // page load retries the merge.
              toast(t('syncError', 'Could not sync your wishlist.'));
            }
          });
      } else if (Array.isArray(CFG.serverIds)) {
        // Free (rendered into the page) — reconcile on every load.
        markMergedAndReconcile(CFG.serverIds);
      } else {
        var hydrated = false;
        try { hydrated = window.sessionStorage.getItem(SESSION_HYDRATED_KEY) === '1'; } catch (e) { /* ignore */ }
        if (!hydrated) {
          hydrateFromServer().catch(function () { /* hearts stay on cached state */ });
        }
      }
    } else {
      var guestStore = readStore();
      if (guestStore.mergedCustomerId != null) {
        // A logged-out customer's cached list must not be shown to (or grown
        // by) guests on a shared machine — start guests from a clean store.
        writeStore({ v: 1, mergedCustomerId: null, items: [] });
      }
      renderGuestGrid();
    }
    paintButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
